/**
 * Hierarchy Context
 *
 * Provides global state for the Client > Matter > Project selection
 * that persists across page navigations. Changing a parent clears children.
 */

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import type { Client, Matter, Project } from '../api/types';

interface HierarchyState {
  selectedClient: Client | null;
  selectedMatter: Matter | null;
  /** The grouping project name (a project can have many versions). */
  selectedProjectName: string | null;
  /** The specific project version; its id is the project-version PK. */
  selectedProject: Project | null;
  /**
   * Bumped whenever client/matter/project data is mutated on a CRUD page.
   * The context bar folds this into its fetch keys so its dropdowns refetch
   * and pick up newly-created rows without a full page reload.
   */
  dataVersion: number;
}

type HierarchyAction =
  | { type: 'SELECT_CLIENT'; payload: Client | null }
  | { type: 'SELECT_MATTER'; payload: Matter | null }
  | { type: 'SELECT_PROJECT_NAME'; payload: string | null }
  | { type: 'SELECT_PROJECT'; payload: Project | null }
  | { type: 'BUMP_DATA' }
  | { type: 'CLEAR_ALL' };

function hierarchyReducer(state: HierarchyState, action: HierarchyAction): HierarchyState {
  switch (action.type) {
    case 'SELECT_CLIENT':
      return {
        ...state,
        selectedClient: action.payload,
        selectedMatter: null,
        selectedProjectName: null,
        selectedProject: null,
      };
    case 'SELECT_MATTER':
      return {
        ...state,
        selectedMatter: action.payload,
        selectedProjectName: null,
        selectedProject: null,
      };
    case 'SELECT_PROJECT_NAME':
      return {
        ...state,
        selectedProjectName: action.payload,
        selectedProject: null,
      };
    case 'SELECT_PROJECT':
      return {
        ...state,
        selectedProject: action.payload,
      };
    case 'BUMP_DATA':
      return {
        ...state,
        dataVersion: state.dataVersion + 1,
      };
    case 'CLEAR_ALL':
      return {
        selectedClient: null,
        selectedMatter: null,
        selectedProjectName: null,
        selectedProject: null,
        dataVersion: state.dataVersion,
      };
    default:
      return state;
  }
}

interface HierarchyContextValue extends HierarchyState {
  selectClient: (client: Client | null) => void;
  selectMatter: (matter: Matter | null) => void;
  selectProjectName: (name: string | null) => void;
  selectProject: (project: Project | null) => void;
  /** Signal that client/matter/project data changed (create/edit/delete). */
  bumpData: () => void;
  clearAll: () => void;
}

const HierarchyContext = createContext<HierarchyContextValue | null>(null);

export function HierarchyProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(hierarchyReducer, {
    selectedClient: null,
    selectedMatter: null,
    selectedProjectName: null,
    selectedProject: null,
    dataVersion: 0,
  });

  const selectClient = useCallback((client: Client | null) => {
    dispatch({ type: 'SELECT_CLIENT', payload: client });
  }, []);

  const selectMatter = useCallback((matter: Matter | null) => {
    dispatch({ type: 'SELECT_MATTER', payload: matter });
  }, []);

  const selectProjectName = useCallback((name: string | null) => {
    dispatch({ type: 'SELECT_PROJECT_NAME', payload: name });
  }, []);

  const selectProject = useCallback((project: Project | null) => {
    dispatch({ type: 'SELECT_PROJECT', payload: project });
  }, []);

  const bumpData = useCallback(() => {
    dispatch({ type: 'BUMP_DATA' });
  }, []);

  const clearAll = useCallback(() => {
    dispatch({ type: 'CLEAR_ALL' });
  }, []);

  return (
    <HierarchyContext.Provider
      value={{ ...state, selectClient, selectMatter, selectProjectName, selectProject, bumpData, clearAll }}
    >
      {children}
    </HierarchyContext.Provider>
  );
}

export function useHierarchy() {
  const context = useContext(HierarchyContext);
  if (!context) {
    throw new Error('useHierarchy must be used within a HierarchyProvider');
  }
  return context;
}
